import type { TrainSubview } from "../features/train/trainViewState";

export const NAVIGATION_STATE_STORAGE_KEY = "johan-performance-lab.navigation.v1";

export type AppPageId =
  | "home"
  | "train"
  | "nutrition"
  | "recovery"
  | "equipment"
  | "intelligence"
  | "settings";

export type RecoverySubview = "daily-health";

export type NavigationStateRecord = {
  version: 1;
  activePage: AppPageId;
  trainSubview: TrainSubview;
  recoverySubview: RecoverySubview;
  selectedDailyHealthDate: string | null;
  updatedAt: string;
};

export type ResolvedNavigationState = {
  state: NavigationStateRecord;
  restorationSource: "url" | "storage" | "default";
  restoredPage: AppPageId;
  fallbackReason: string | null;
  explicitUrlRouteTookPrecedence: boolean;
};

export type PersistedNavigationReadResult = {
  state: NavigationStateRecord | null;
  hadRawValue: boolean;
  fallbackReason: string | null;
};

const VALID_PAGES: AppPageId[] = ["home", "train", "nutrition", "recovery", "equipment", "intelligence", "settings"];
const VALID_TRAIN_SUBVIEWS: TrainSubview[] = ["overview", "timeline", "analysis"];

export function defaultNavigationState(): NavigationStateRecord {
  return {
    version: 1,
    activePage: "home",
    trainSubview: "overview",
    recoverySubview: "daily-health",
    selectedDailyHealthDate: null,
    updatedAt: new Date().toISOString()
  };
}

function isValidPage(value: unknown): value is AppPageId {
  return typeof value === "string" && VALID_PAGES.includes(value as AppPageId);
}

function coerceTrainSubview(value: unknown): TrainSubview {
  if (typeof value === "string" && VALID_TRAIN_SUBVIEWS.includes(value as TrainSubview)) {
    return value as TrainSubview;
  }
  return "overview";
}

function withTimestamp(state: Omit<NavigationStateRecord, "version" | "updatedAt">): NavigationStateRecord {
  return {
    version: 1,
    ...state,
    updatedAt: new Date().toISOString()
  };
}

export function navigationStateFromUnknown(value: unknown): NavigationStateRecord | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const candidate = value as Record<string, unknown>;
  if (!isValidPage(candidate.activePage)) {
    return null;
  }

  return withTimestamp({
    activePage: candidate.activePage,
    trainSubview: coerceTrainSubview(candidate.trainSubview),
    recoverySubview: "daily-health",
    selectedDailyHealthDate: typeof candidate.selectedDailyHealthDate === "string" ? candidate.selectedDailyHealthDate : null
  });
}

export function readPersistedNavigationState(storage?: Storage): NavigationStateRecord | null {
  return readPersistedNavigationStateWithDiagnostics(storage).state;
}

export function readPersistedNavigationStateWithDiagnostics(storage?: Storage): PersistedNavigationReadResult {
  try {
    const source = storage ?? (typeof window !== "undefined" ? window.localStorage : undefined);
    if (!source) {
      return { state: null, hadRawValue: false, fallbackReason: "storage-unavailable" };
    }
    const raw = source.getItem(NAVIGATION_STATE_STORAGE_KEY);
    if (!raw) {
      return { state: null, hadRawValue: false, fallbackReason: null };
    }
    const parsed: unknown = JSON.parse(raw);
    const state = navigationStateFromUnknown(parsed);
    if (!state) {
      return { state: null, hadRawValue: true, fallbackReason: "invalid-persisted-navigation" };
    }
    return { state, hadRawValue: true, fallbackReason: null };
  } catch {
    return { state: null, hadRawValue: true, fallbackReason: "invalid-persisted-navigation" };
  }
}

export function writePersistedNavigationState(state: NavigationStateRecord, storage?: Storage): void {
  try {
    const target = storage ?? (typeof window !== "undefined" ? window.localStorage : undefined);
    if (!target) return;
    target.setItem(NAVIGATION_STATE_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // No-op in constrained storage environments.
  }
}

export function parseNavigationStateFromUrlHash(hash: string): NavigationStateRecord | null {
  const normalized = hash.trim();
  if (!normalized.startsWith("#/")) {
    return null;
  }

  const path = normalized.slice(2);
  const [pageRaw, subviewRaw] = path.split("/");
  if (!isValidPage(pageRaw)) {
    return null;
  }

  if (pageRaw === "train") {
    return withTimestamp({
      activePage: "train",
      trainSubview: coerceTrainSubview(subviewRaw),
      recoverySubview: "daily-health",
      selectedDailyHealthDate: null
    });
  }

  if (pageRaw === "recovery") {
    return withTimestamp({
      activePage: "recovery",
      trainSubview: "overview",
      recoverySubview: "daily-health",
      selectedDailyHealthDate: null
    });
  }

  return withTimestamp({
    activePage: pageRaw,
    trainSubview: "overview",
    recoverySubview: "daily-health",
    selectedDailyHealthDate: null
  });
}

export function buildNavigationHash(state: Pick<NavigationStateRecord, "activePage" | "trainSubview">): string {
  if (state.activePage === "train") {
    return `#/train/${state.trainSubview}`;
  }
  return `#/${state.activePage}`;
}

export function resolveInitialNavigationState(input: {
  hash: string;
}): ResolvedNavigationState {
  // ONLY check explicit URL hash for deep linking.
  // Never restore persisted navigation from localStorage on app initialization.
  // This ensures consistent, deterministic startup behavior.
  
  const fromUrl = parseNavigationStateFromUrlHash(input.hash);
  if (fromUrl) {
    return {
      state: fromUrl,
      restorationSource: "url",
      restoredPage: fromUrl.activePage,
      fallbackReason: null,
      explicitUrlRouteTookPrecedence: true
    };
  }

  // On any full app initialization without explicit URL route, default to Home.
  return {
    state: defaultNavigationState(),
    restorationSource: "default",
    restoredPage: "home",
    fallbackReason: null,
    explicitUrlRouteTookPrecedence: false
  };
}
